import { CheckCircle2, Clock, Plus, X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const LIFECYCLE_LABELS = {
  active: "Added",
  completed: "Completed",
  snoozed: "Snoozed",
}

export function ConceptExecutionActionCard({
  action,
  disabled = false,
  onAdd,
  onDismiss,
}) {
  const lifecycleLabel = LIFECYCLE_LABELS[action.actionState]
  const isProposal = !lifecycleLabel

  return (
    <article className="rounded-lg border border-border bg-background p-4">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_22rem] md:items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h4 className="text-sm font-bold text-foreground">{action.title}</h4>
            {lifecycleLabel ? (
              <Badge variant="secondary">
                {action.actionState === "snoozed" ? <Clock aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}
                {lifecycleLabel}
              </Badge>
            ) : null}
          </div>
          <dl className="mt-4 md:hidden">
            <ExecutionMetric label="Confidence" value={shortConfidence(action.confidence)} />
          </dl>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <ExecutionMetric label="Impact" value={action.impact} />
          <ExecutionMetric label="Cost" value={action.cost} />
          <ExecutionMetric label="Complexity" value={action.complexity} />
          <ExecutionMetric label="Value Uplift" value={action.valueUplift} />
          <ExecutionMetric
            label="Confidence"
            value={shortConfidence(action.confidence)}
            className="hidden sm:block"
          />
        </dl>
      </div>

      {isProposal ? (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            aria-label={`Add ${action.title} to Priority Actions`}
            onClick={() => onAdd?.(action)}
          >
            <Plus aria-hidden="true" />
            Add
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            aria-label={`Dismiss ${action.title}`}
            onClick={() => onDismiss?.(action)}
          >
            <X aria-hidden="true" />
            Dismiss
          </Button>
        </div>
      ) : null}
    </article>
  )
}

function ExecutionMetric({ label, value, className }) {
  return (
    <div className={cn("min-w-0", className)}>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className={cn("mt-1 text-xs font-bold text-foreground", label === "Value Uplift" && "text-primary")}>
        {value}
      </dd>
    </div>
  )
}

function shortConfidence(value) {
  return String(value ?? "Pending").replace(/\s*Confidence$/i, "")
}
